// ─────────────────────────────────────────────────────────────
// EventCalendar.js
// Self-contained calendar component. Owns all state related to:
//   - Which month is displayed
//   - Which event is selected
//   - RSVPs for the selected event (real-time Firestore listener)
//   - RSVP form state (name, response, plusCount)
//   - Existing RSVP detection (phone hash primary, deviceId secondary)
//
// Identity hierarchy (most → least reliable):
//   1. phoneToken — cross-device, cross-browser (HMAC issued by Cloud Functions)
//   2. deviceId   — persistent UUID in localStorage, single browser
//   3. fingerprint — audit/debug logs only, NOT used for identity
//
// Banning: IP address + expiration date (fingerprint not used)
//
// Props:
//   locations  — map of locationId -> location object (from RSVPPage)
//   adminUser  — Firebase auth user or null
//   showToast  — (message, type) => void
//   onConfirm  — admin confirm handler
//   onCancel   — admin cancel handler
//   onSaveAttendance — admin attendance save handler
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import {
  collection, addDoc, query, where, getDocs, getDoc,
  onSnapshot, orderBy, Timestamp, updateDoc, doc, setDoc
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getBrowserFingerprint } from '../utils/fingerprint';
import { getDeviceInfo, getSessionId, getDeviceId } from '../utils/deviceInfo';
import { format } from 'date-fns';
import AdminToolbar from './AdminToolbar';
import { banKey } from '../utils/banKey';
import WeatherWidget from './WeatherWidget';
import CalendarButtons from './CalendarButtons';
import { googleCalendarUrl, generateSeriesICS, downloadICS } from '../utils/calendarUtils';
import { trackRsvp, trackSmsSubscribed, trackSmsUnsubscribed, trackCalendarGoogle, trackCalendarDownload, trackWeatherClicked, trackShareLink } from '../utils/analytics';
import SITE from '../site';

// ─── EVENT END TIME ───────────────────────────────────────────
// Returns a Date representing when the event ends.
// When endTime is set (e.g. "20:30") it is used directly.
// When endTime is absent, assume 3 hours after start so events
// are considered "over" at a reasonable time rather than at 23:59.
const getEventEndTime = (e) => {
  const start = e.date?.toDate ? e.date.toDate() : new Date(e.date);
  if (e.endTime) {
    const [h, m] = e.endTime.split(':').map(Number);
    const end = new Date(start);
    end.setHours(h, m, 0, 0);
    return end;
  }
  return new Date(start.getTime() + 3 * 60 * 60 * 1000);
};

const formatEventLabel = (event) => {
  if (!event) return null;
  try {
    const d = event.date?.toDate ? event.date.toDate() : new Date(event.date);
    return `${event.title} — ${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'America/Los_Angeles' })}`;
  } catch { return event.title || null; }
};

function EventCalendar({ locations, adminUser, showToast, onConfirm, onCancel, onSaveAttendance, onRegisterRefresh, showWeather, disclaimer, minPlayers, onEventSelect, onStripUpdate, lastAttendance, siteUrl, statusLines, recurringSettings, calendarDescription, deepLinkedEventId }) {

  // ─── CALENDAR STATE ──────────────────────────────────────────
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [nextEventId, setNextEventId] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [pulseCard, setPulseCard] = useState(false);

  // ─── RSVP STATE ──────────────────────────────────────────────
  const [rsvps, setRsvps] = useState([]);           // RSVPs for selected event
  const [hasRSVPd, setHasRSVPd] = useState(false);
  const [existingRSVP, setExistingRSVP] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [whosComingOpen, setWhosComingOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const dropdownRef = useRef(null);
  const nameRef = useRef(null);
  const nameTimerRef = useRef(null);
  // Serializes the write half of handleSubmit — see the note there.
  const submitLockRef = useRef(Promise.resolve());

  // ─── FORM STATE ───────────────────────────────────────────────
  const [name, setName] = useState(localStorage.getItem('rsvp_user_name') || '');
  const [response, setResponse] = useState('');
  const [plusCount, setPlusCount] = useState(0);

  // ─── SMS OPT-IN STATE ────────────────────────────────────────
  // Phone stored in localStorage may be raw digits or E164 — format for display.
  // formatPhoneInput is defined inside the component below; we inline the same
  // logic here for the one-time initialiser so it runs before any render.
  const _initPhone = (() => {
    let d = (localStorage.getItem('rsvp_sms_phone') || '').replace(/\D/g, '');
    if (d.startsWith('1') && d.length === 11) d = d.slice(1);
    d = d.slice(0, 10);
    if (d.length === 0) return '';
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  })();
  const [smsPhone, setSmsPhone] = useState(_initPhone);
  // Ref mirrors smsPhone but updates synchronously in the onChange handler,
  // so handleSubmit always reads the latest value even when autofill and a
  // button click race each other before React re-renders.
  const smsPhoneRef = useRef(_initPhone);
  // ─── SMS SOCIAL PROOF ────────────────────────────────────────
  // Group-level count of opted-in subscribers, read from a public integer that
  // Cloud Functions maintains (the subscribers collection itself is admin-only).
  // Deliberately NOT a ratio of this event's RSVPs: early in the RSVP period that
  // reads "2 of 3", which advertises how few people have replied.
  // Hidden below the floor so a quiet spell never argues against signing up.
  const SOCIAL_PROOF_FLOOR = 25;
  const [subscriberCount, setSubscriberCount] = useState(null);
  const showJoinedPill = subscriberCount != null && subscriberCount >= SOCIAL_PROOF_FLOOR;
  // The carrier-facing consent disclosure, shown wherever someone can opt in —
  // the unlocked card's checkbox and the locked card's accordion, which is also a
  // subscribe point and previously carried only "Reply STOP anytime". Toll-free
  // verification reviewers look for the sending brand and the message frequency
  // at the point of consent, so both places must state them.
  //
  // One constant, two render sites, deliberately: this wording is cited in the
  // filing and mirrored in TermsModal section 2, receiveSms and the privacy
  // policy, so a second copy that could drift is a compliance problem rather
  // than a tidiness one. The shorter "Reply STOP to unsubscribe. Msg & data
  // rates may apply." can come back here once the number is approved.
  const SMS_DISCLOSURE = SITE.smsDisclosure;
  const joinedPillStyle = {
    fontSize: '11px', background: 'rgba(120,170,240,.22)', color: '#bcd3f5',
    padding: '1px 7px', borderRadius: '999px', whiteSpace: 'nowrap',
  };
  // Marks a player in Who's Coming as reachable by text. It sits in the column
  // the avatar used to occupy, so the marks form one vertical line instead of
  // landing at ragged positions after names of different lengths.
  //
  // A glyph, not a dot, and this is the whole point — two separate misreads both
  // come from the mark being a small filled circle:
  //
  // 1. Presence. A coloured circle beside a name, in the avatar slot, leading,
  //    in a status colour is the Slack/Teams/Discord "user is online" pattern,
  //    and members said so unprompted. Recolouring cannot escape it: the status
  //    palette is fully occupied (green online, amber away, grey offline), which
  //    is why the amber this replaced had the same problem wearing a different
  //    state. A filled mark paired with a muted twin is the strongest cue of the
  //    set, because presence systems always render a state for everyone.
  // 2. Bullets. A small filled circle at the start of a line IS the bullet
  //    character, so the block parses as a bulleted list and rows without one
  //    read as continuations of the row above. An earlier amber-with-no-
  //    placeholder version reserved the slot width and still looked broken,
  //    which is the proof that this is glyph semantics and not layout.
  //
  // A message square is neither: nothing sets bullets in speech bubbles, and no
  // presence indicator is bubble-shaped. That is what lets the placeholder go,
  // and dropping the placeholder is what removes the online/offline pair.
  //
  // 13px, not 8: bullet-scale reads as punctuation, icon-scale reads as a badge.
  // It still fits inside the ~18px line box, so row height is unchanged and only
  // the marker column widens (~5px, against the 44px avatar that used to be here).
  //
  // No outline needed. The row sits on .evt-state-band, whose background is one
  // of five hardcoded dark values (#0f2a1a game on, #2a1a00 close/decision,
  // #1a2535 default/locked, #1e2a36 past, #1c1c1c canceled) — the teal reads well
  // on all of them. Note those are NOT the DEFAULT_COLORS in AdminTabGeneral:
  // those brighter values (#d97706, #16a34a …) are the flag chip and status-text
  // colours, not the card fill. If the band ever gains a light background, this
  // mark needs a ring to survive it.
  //
  // Deliberately not a check mark: nothing verifies the number is really theirs.
  const SMS_MARK_COLOR = '#6BACBD';
  const smsMarkGlyph = (extra) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill={SMS_MARK_COLOR}
      style={{flexShrink: 0, display: 'block', ...extra}} aria-hidden="true">
      <path d="M3 4h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H8l-5 4V5a1 1 0 0 1 1-1z" />
    </svg>
  );
  // Rows with no subscriber render an empty slot of identical width so names stay
  // flush. Nothing is drawn in it: a visible placeholder would rebuild the
  // two-state pair this change exists to remove, and it would also disclose a
  // non-membership the app doesn't otherwise publish — which is why neither the
  // slot nor anything in it carries a title.
  const smsSlotStyle = { width: '13px', height: '13px', flexShrink: 0 };
  const smsMark = (on) => on
    ? <span title="Gets game-day texts" style={{display: 'flex', flexShrink: 0}}>{smsMarkGlyph()}</span>
    : <span style={smsSlotStyle} />;

  useEffect(() => {
    getDoc(doc(db, 'publicStats', 'subscribers'))
      .then(s => { if (s.exists()) setSubscriberCount(s.data().optedInCount ?? null); })
      .catch(() => {});
  }, []);

  // Caches { phone, info } from getSubscriptionStatus so a single page session
  // doesn't re-request the token on every keystroke or submit.
  const smsTokenRef = useRef(null);
  const [savedIndicator, setSavedIndicator] = useState(false);
  const savedTimerRef = useRef(null);
  const [lockAccordion, setLockAccordion] = useState(null); // 'sms' | 'calendar' | null
  const [smsOptIn, setSmsOptIn] = useState(localStorage.getItem('rsvp_sms_opted_in') === '1');
  const [smsStatus, setSmsStatus] = useState(localStorage.getItem('rsvp_sms_opted_in') === '1' ? 'opted-in' : null);
  // The dot in Who's Coming reads `smsOptedIn` straight off the RSVP doc.
  // Nothing here reads the subscriber list: that roster download was the original
  // PII leak, and a boolean beside an already-public name discloses nothing more
  // than the marker itself does. scrubRsvpPii keeps the flag true to the
  // subscribers collection for upcoming events.

  // On mount: if phone already stored, verify subscriber status from Firestore
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const stored = localStorage.getItem('rsvp_sms_phone');
    if (stored) lookupSubscriber(stored);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── LOCATION POPUP STATE ────────────────────────────────────
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [popupLocations, setPopupLocations] = useState([]);

  // ─── LOAD CALENDAR EVENTS FOR CURRENT MONTH ──────────────────
  const loadCalendarEvents = async (date, autoSelect = false) => {
    try {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
      const q = query(
        collection(db, 'events'),
        where('date', '>=', Timestamp.fromDate(start)),
        where('date', '<=', Timestamp.fromDate(end)),
        orderBy('date', 'asc')
      );
      const snap = await getDocs(q);
      const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCalendarEvents(events);

      if (autoSelect) {
        const now = new Date();
        // Deep link takes priority — find event by ID and navigate to its month
        if (deepLinkedEventId) {
          const linked = events.find(e => e.id === deepLinkedEventId);
          if (linked) {
            setSelectedEvent(linked);
            const d = linked.date.toDate();
            setCalendarDate(new Date(d.getFullYear(), d.getMonth(), 1));
            return;
          }
        }
        // Select the event whose end time is nearest in the future
        // (allows selecting a game that's currently in progress)
        const upcoming = events
          .filter(e => getEventEndTime(e) > now)
          .sort((a, b) => getEventEndTime(a) - getEventEndTime(b));
        if (upcoming.length > 0) {
          setSelectedEvent(upcoming[0]);
        } else {
          setSelectedEvent(null);
        }
      }
    } catch (error) {
      console.error('Error loading calendar events:', error);
    }
  };

  // ─── REFRESH SINGLE EVENT ────────────────────────────────────
  // Called by RSVPPage after admin confirm/cancel writes to Firestore.
  // Re-fetches the event and updates selectedEvent + calendarEvents in place
  // so the UI reflects the change without a full page reload.
  const refreshEvent = async (eventId) => {
    try {
      const snap = await getDoc(doc(db, 'events', eventId));
      if (!snap.exists()) return;
      const updated = { id: snap.id, ...snap.data() };
      // Update selectedEvent if it's the same event
      setSelectedEvent(prev => prev?.id === eventId ? updated : prev);
      // Update the event in calendarEvents list
      setCalendarEvents(prev => prev.map(e => e.id === eventId ? updated : e));
    } catch (error) {
      console.error('Error refreshing event:', error);
    }
  };

  // ─── INITIAL LOAD ─────────────────────────────────────────────
  // Loads current month. If no upcoming event found, searches forward
  // up to 3 months to find and auto-select the next event.
  useEffect(() => {
    const init = async () => {
      const now = new Date();

      // ── DEEP LINK: fetch the specific event directly by ID ──────
      if (deepLinkedEventId) {
        try {
          const snap = await getDoc(doc(db, 'events', deepLinkedEventId));
          if (snap.exists()) {
            const linked = { id: snap.id, ...snap.data() };
            const d = linked.date.toDate();
            const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
            // Load that month's events for the calendar grid, then select
            const mq = query(
              collection(db, 'events'),
              where('date', '>=', Timestamp.fromDate(monthStart)),
              where('date', '<=', Timestamp.fromDate(new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59))),
              orderBy('date', 'asc')
            );
            const msnap = await getDocs(mq);
            const mevents = msnap.docs.map(e => ({ id: e.id, ...e.data() }));
            setCalendarEvents(mevents);
            setCalendarDate(monthStart);
            setSelectedEvent(linked);
            // Still compute nextEventId from current month so the "Next game" eyebrow works
            const nowMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const nowMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
            const nq = query(collection(db, 'events'), where('date', '>=', Timestamp.fromDate(nowMonth)), where('date', '<=', Timestamp.fromDate(nowMonthEnd)), orderBy('date', 'asc'));
            const nsnap = await getDocs(nq);
            const nevents = nsnap.docs.map(e => ({ id: e.id, ...e.data() }));
            const nexte = nevents.filter(e => getEventEndTime(e) > now).sort((a,b) => getEventEndTime(a)-getEventEndTime(b))[0] || null;
            if (nexte) setNextEventId(nexte.id);
            return;
          }
        } catch (e) {
          console.warn('Deep link event not found, falling through to default:', e);
        }
      }

      // ── DEFAULT: Load current month and find next upcoming event ─
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      const q = query(
        collection(db, 'events'),
        where('date', '>=', Timestamp.fromDate(start)),
        where('date', '<=', Timestamp.fromDate(end)),
        orderBy('date', 'asc')
      );
      const snap = await getDocs(q);
      const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const next = events.filter(e => getEventEndTime(e) > now).sort((a,b) => getEventEndTime(a)-getEventEndTime(b))[0] || null;

      if (next) {
        // Found an upcoming event in the current month
        setCalendarEvents(events);
        setSelectedEvent(next);
        setNextEventId(next.id);
      } else {
        // No upcoming event this month — search forward month by month
        let found = false;
        for (let m = 1; m <= 6; m++) {
          const fStart = new Date(now.getFullYear(), now.getMonth() + m, 1);
          const fEnd   = new Date(now.getFullYear(), now.getMonth() + m + 1, 0, 23, 59, 59);
          const fq = query(
            collection(db, 'events'),
            where('date', '>=', Timestamp.fromDate(fStart)),
            where('date', '<=', Timestamp.fromDate(fEnd)),
            orderBy('date', 'asc')
          );
          const fsnap = await getDocs(fq);
          const fevents = fsnap.docs.map(d => ({ id: d.id, ...d.data() }));
          const fnext = fevents.filter(e => getEventEndTime(e) > now).sort((a,b) => getEventEndTime(a)-getEventEndTime(b))[0] || null;
          if (fnext) {
            const navDate = new Date(now.getFullYear(), now.getMonth() + m, 1);
            setCalendarDate(navDate);
            setCalendarEvents(fevents);
            setSelectedEvent(fnext);
            setNextEventId(fnext.id);
            found = true;
            break;
          }
        }
        if (!found) {
          setCalendarEvents(events);
        }
      }
    };
    init();
  }, []);

  // Register refreshEvent with parent so it can trigger it after Firestore writes
  useEffect(() => {
    if (onRegisterRefresh) onRegisterRefresh(refreshEvent);
  }, []);

  // Autofocus name field only when expansion opens AND name is empty
  useEffect(() => {
    if ((response === 'yes' || response === 'maybe' || response === 'no') && !name.trim() && nameRef.current) {
      setTimeout(() => nameRef.current?.focus(), 300);
    }
  }, [response]);

  // When name is typed after clicking a button (no prior name),
  // submit the RSVP once they've entered enough characters
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (name.trim().length >= 2 && (response === 'yes' || response === 'maybe' || response === 'no') && !hasRSVPd) {
      clearTimeout(nameTimerRef.current);
      nameTimerRef.current = setTimeout(() => {
        if (name.trim() && !hasRSVPd) {
          handleSubmit(response);
        }
      }, 800);
    }
  }, [name]);

  // Close who's coming dropdown on outside click
  useEffect(() => {
    if (!whosComingOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setWhosComingOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [whosComingOpen]);

  // ─── NOTIFY PARENT OF SELECTED EVENT + STRIP DATA ───────────
  useEffect(() => {
    if (onEventSelect) {
      onEventSelect(
        selectedEvent ? (selectedEvent.title || 'Weekly Event') : '',
        selectedEvent ? selectedEvent.id === nextEventId : false
      );
    }
    if (onStripUpdate && selectedEvent) {
      const now = new Date();
      const eventDate = selectedEvent.date.toDate();
      const isEventPast = getEventEndTime(selectedEvent) < now;

      if (isEventPast || selectedEvent.isCanceled || selectedEvent.isConfirmed) {
        onStripUpdate(null);
        return;
      }
      const min = minPlayers || 8;
      const yesT = rsvps.filter(r => r.response === 'yes')
        .reduce((s, r) => s + 1 + (r.plusCount || 0), 0);
      const maybeRsvps = rsvps.filter(r => r.response === 'maybe');
      const yesRsvpsList = rsvps.filter(r => r.response === 'yes');
      const needed = Math.max(0, min - yesT);
      const msUntil = eventDate - now;
      const daysUntil = Math.floor(msUntil / (1000 * 60 * 60 * 24));
      const hoursUntil = Math.floor((msUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      // Stable shuffle
      const shuffledYes = [...yesRsvpsList].sort((a, b) => {
        const seed = selectedEvent.id.charCodeAt(0) + selectedEvent.id.charCodeAt(1);
        return (a.id.charCodeAt(0) + seed) % 7 - (b.id.charCodeAt(0) + seed) % 7;
      });
      const MAX_PILLS = 3;
      const yesSlots = Math.min(shuffledYes.length, MAX_PILLS);
      const maybeSlots = Math.min(maybeRsvps.length, MAX_PILLS - yesSlots);
      const pills = [
        ...shuffledYes.slice(0, yesSlots).map(r => ({ name: r.name.split(' ')[0], maybe: false })),
        ...maybeRsvps.slice(0, maybeSlots).map(r => ({ name: r.name.split(' ')[0], maybe: true })),
      ];
      const overflowCount = (yesRsvpsList.length - yesSlots) + (maybeRsvps.length - maybeSlots);
      onStripUpdate({
        progressPct: min > 0 ? Math.min(100, Math.round((yesT / min) * 100)) : 0, needed, min, almostThreshold: 2,
        yesTotal: yesT,
        daysUntil, hoursUntil, pills, overflowCount,
        whosComingOpen,
        onToggle: () => setWhosComingOpen(prev => !prev),
      });
    } else if (onStripUpdate) {
      onStripUpdate(null);
    }
  }, [selectedEvent, nextEventId, rsvps, whosComingOpen]);

  // ─── RSVP LISTENER — fires when selected event changes ───────
  // Creates a fresh real-time listener. Cleans up on change.
  useEffect(() => {
    if (!selectedEvent) {
      setRsvps([]);
      setHasRSVPd(false);
      setExistingRSVP(null);
      setResponse('');
      setPlusCount(0);
      setWhosComingOpen(false);
    setDescExpanded(false);
      return;
    }

    const eventId = selectedEvent.id;

    // Start real-time RSVP listener
    const q = query(
      collection(db, 'rsvps'),
      where('eventId', '==', eventId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const seen = new Set();
      const data = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });
      setRsvps(data);
    }, (error) => {
      console.error('RSVP listener error:', error);
    });

    // Check if current user has already RSVPd
    checkExistingRSVP(eventId);

    // Reset Who's Coming and description when switching events
    setWhosComingOpen(false);
    setDescExpanded(false);

    return () => unsubscribe();
  }, [selectedEvent?.id]);

  // ─── CHECK EXISTING RSVP ─────────────────────────────────────
  // Identity priority: phone hash (cross-device) → deviceId (UUID in localStorage).
  // No raw-phone fallback — RSVP docs are world-readable and store no phone number.
  // Fingerprint is no longer used for identity — only stored in audit logs.
  const checkExistingRSVP = async (eventId) => {
    try {
      const deviceId = getDeviceId();
      const currentPhone = smsPhone || localStorage.getItem('rsvp_sms_phone') || '';
      let found = null;

      // 1. Phone token — most reliable, works across browsers and devices
      if (currentPhone) {
        try {
          const info = await resolvePhone(currentPhone);
          if (info?.token) {
            const phoneSnap = await getDocs(query(
              collection(db, 'rsvps'),
              where('eventId', '==', eventId),
              where('phoneToken', '==', info.token)
            ));
            if (!phoneSnap.empty) found = { id: phoneSnap.docs[0].id, ...phoneSnap.docs[0].data() };
          }
        } catch (e) { console.warn('Phone RSVP lookup failed:', e); }
      }

      // 2. Device ID — persistent UUID in localStorage, single-browser fallback
      if (!found) {
        const deviceSnap = await getDocs(query(
          collection(db, 'rsvps'),
          where('eventId', '==', eventId),
          where('deviceId', '==', deviceId)
        ));
        if (!deviceSnap.empty) found = { id: deviceSnap.docs[0].id, ...deviceSnap.docs[0].data() };
      }

      if (found) {
        setHasRSVPd(true);
        setExistingRSVP(found);
        setResponse(found.response);
        setPlusCount(found.plusCount || 0);
        // Name is filled only for a browser we already recognise — see the note
        // on the same guard in the phone-lookup path below.
        if (found.name && found.deviceId && found.deviceId === deviceId) setName(found.name);
      } else {
        setHasRSVPd(false);
        setExistingRSVP(null);
        setResponse('');
      }
    } catch (error) {
      console.error('Error checking existing RSVP:', error);
    }
  };

  // ─── RSVP SUBMIT ─────────────────────────────────────────────
  // IMPORTANT: We query Firestore directly at submit time to check
  // for an existing RSVP, rather than relying on React state.
  // This prevents race conditions where stale state causes duplicate
  // RSVPs when clicking quickly or when state hasn't settled after
  // a delete operation.
  // RSVP deletes run server-side: the collection no longer allows unauthenticated
  // deletes (anyone with the public API key could previously wipe it), and the
  // callable writes the audit entry so a client cannot skip logging its own delete.
  const callDeleteRsvp = async (rsvpId, { reason, phoneToken, metadata } = {}) => {
    await httpsCallable(getFunctions(), 'deleteRsvp')({
      rsvpId,
      deviceId: getDeviceId(),
      phoneToken: phoneToken || null,
      entityLabel: selectedEvent ? formatEventLabel(selectedEvent) : null,
      reason: reason || null,
      metadata: metadata || null,
    });
  };

  // Subscriber docs are admin-write-only now, so the lastRsvpAt stamp goes
  // through the same callable as the rest of the public subscription writes.
  const touchSubscriberRsvp = (e164) => {
    if (!e164) return;
    httpsCallable(getFunctions(), 'updateSubscription')({ phone: e164, action: 'touchRsvp' })
      .catch(() => {});
  };

  const handleUnrsvp = async () => {
    if (!existingRSVP) return;
    try {
      const deviceInfo = await getDeviceInfo();
      const fingerprint = await getBrowserFingerprint(); // audit log only
      const sessionId = getSessionId();
      const deviceId  = getDeviceId();
      // Deleted server-side — the callable verifies ownership and writes the
      // audit entry itself (see deleteRsvp in functions/index.js).
      await callDeleteRsvp(existingRSVP.id, {
        phoneToken: existingRSVP.phoneToken || null,
        metadata: {
          source: 'web',
          deviceInfo: { fingerprint, sessionId, deviceId,
            ipAddress: deviceInfo.ipAddress, location: deviceInfo.location,
            userAgent: deviceInfo.userAgent, platform: deviceInfo.platform,
            screenResolution: deviceInfo.screenResolution, timezone: deviceInfo.timezone,
            language: deviceInfo.language, referrer: deviceInfo.referrer,
            connection: deviceInfo.connection },
        },
      });
      setHasRSVPd(false);
      setExistingRSVP(null);
      setResponse('');
      showToast('RSVP removed');
    } catch (error) {
      console.error('Error removing RSVP:', error);
      showToast('Error removing RSVP', 'error');
    }
  };

  const handleSubmit = async (responseVal) => {
    // If tapping the already-selected response, un-RSVP (whether saved or still awaiting name)
    if (responseVal === response && (hasRSVPd || !name.trim())) {
      if (hasRSVPd) handleUnrsvp();
      else { setResponse(''); }
      return;
    }
    if (!selectedEvent) return;

    // If no name yet, open the expansion and focus the name field
    // The RSVP is optimistically set so the panel opens, but we
    // wait for a name before actually writing to Firestore.
    // Exception: "no" has no name field in the UI (we don't ask
    // non-attendees to identify themselves), so it must submit
    // immediately below with a fallback name rather than stall forever.
    if (!name.trim()) {
      setResponse(responseVal);
      setTimeout(() => nameRef.current?.focus(), 300);
      return;
    }

    const eventId = selectedEvent.id;
    setSubmitting(true);

    // One write at a time. Two callers can overlap here: the auto-submit that
    // fires when you type your name after choosing a response, and a tap on the
    // response button. Each spends up to a second on device/IP lookups before
    // asking whether this device already has an RSVP, so two overlapping calls
    // both got "no" and both created one — that's how "Char" and "Charles"
    // became two RSVPs for the 9/19 game. Queued behind the first, the second
    // finds that RSVP and updates it instead. Every exit below, including the
    // early return for a banned IP, passes through `finally`, which releases.
    //
    // A ref rather than the `submitting` state: state is read from this call's
    // render, so a second call can't see that the first already set it.
    const prior = submitLockRef.current;
    let releaseLock;
    submitLockRef.current = new Promise((resolve) => { releaseLock = resolve; });
    await prior;

    try {
      const [deviceInfo, fingerprint] = await Promise.all([getDeviceInfo(), getBrowserFingerprint()]);
      const sessionId = getSessionId();
      const deviceId = getDeviceId();
      // Fallback for "no" responses from users we don't yet know the name of —
      // there's no name field for "no", so this is the only identity we have.
      const submitName = name.trim() || 'Guest';
      if (name.trim()) localStorage.setItem('rsvp_user_name', name.trim());

      // ── IP ban check ──────────────────────────────────────────
      // Bans are IP-based with an expiration date. Expired bans are ignored.
      if (deviceInfo.ipAddress && deviceInfo.ipAddress !== 'unavailable') {
        // Fetched by hashed doc ID — blockedUsers holds IPs and names, so the
        // collection is not listable; a visitor can only check their own key.
        const banSnap = await getDoc(doc(db, 'blockedUsers', await banKey('ip', deviceInfo.ipAddress)));
        if (banSnap.exists()) {
          const ban = banSnap.data();
          const isExpired = ban.expiresAt && ban.expiresAt.toDate() < new Date();
          if (!isExpired) {
            showToast('You have been blocked from RSVPing.', 'error');
            setSubmitting(false);
            return;
          }
        }
      }

      // ── Identity lookup ───────────────────────────────────────
      // Priority: phone hash → deviceId. The Cloud Function stamps the same
      // phoneHash on SMS RSVPs, so one query catches those too.
      // Fingerprint is retained only in audit log metadata for debugging.
      let existingDoc = null;
      // Use ref so we always get the latest phone value even when autofill
      // and a button click race before React re-renders (stale closure issue).
      const currentPhone = smsPhoneRef.current || smsPhone || localStorage.getItem('rsvp_sms_phone') || '';
      // Normalize to E164 once — used for both Firestore queries and writes.
      const currentPhoneE164 = currentPhone ? toE164(currentPhone) : '';
      let hashedPhone = null;

      // 1. Phone token (server-issued; see resolvePhone)
      if (currentPhone) {
        try {
          const info = await resolvePhone(currentPhone);
          hashedPhone = info?.token || null;
          if (hashedPhone) {
            const phoneSnap = await getDocs(query(
              collection(db, 'rsvps'),
              where('eventId', '==', eventId),
              where('phoneToken', '==', hashedPhone)
            ));
            if (!phoneSnap.empty) existingDoc = phoneSnap.docs[0];
          }
        } catch (e) { console.warn('Phone dedup check failed:', e); }
      }

      // 2. Device ID (if no phone match)
      if (!existingDoc) {
        const deviceSnap = await getDocs(query(
          collection(db, 'rsvps'),
          where('eventId', '==', eventId),
          where('deviceId', '==', deviceId)
        ));
        if (!deviceSnap.empty) existingDoc = deviceSnap.docs[0];
      }

      if (existingDoc) {
        // ── Update existing RSVP ───────────────────────────────
        await updateDoc(doc(db, 'rsvps', existingDoc.id), {
          response: responseVal,
          plusCount: parseInt(plusCount),
          name: submitName,
          deviceId,
          // No phone / ipAddress / fingerprint: RSVP docs are world-readable and
          // Firestore has no field-level read rules (see firestore.rules).
          // phoneToken is the identity key; IP and device details go to auditLogs,
          // which only admins can read.
          ...(hashedPhone ? { phoneToken: hashedPhone } : {}),
          // Drives the dot. Set optimistically so the person who just
          // opted in sees it right away; scrubRsvpPii reconciles it server-side.
          smsOptedIn: smsOptIn === true,
          updatedAt: Timestamp.now()
        });
        await addDoc(collection(db, 'auditLogs'), {
          actorType: 'user',
          actorId: deviceId,
          actorName: submitName,
          action: 'rsvp_updated',
          entityType: 'rsvp',
          entityId: existingDoc.id,
          entityLabel: formatEventLabel(selectedEvent),
          before: { response: existingDoc.data().response || null, plusCount: existingDoc.data().plusCount ?? 0 },
          after: { response: responseVal, plusCount: parseInt(plusCount) },
          metadata: {
            source: 'web',
            phoneToken: hashedPhone || null,
            deviceInfo: { fingerprint, sessionId, deviceId,
              ipAddress: deviceInfo.ipAddress, location: deviceInfo.location,
              userAgent: deviceInfo.userAgent, platform: deviceInfo.platform,
              screenResolution: deviceInfo.screenResolution, timezone: deviceInfo.timezone,
              language: deviceInfo.language, referrer: deviceInfo.referrer,
              connection: deviceInfo.connection },
          },
          timestamp: Timestamp.now()
        });
        touchSubscriberRsvp(currentPhoneE164);
        setHasRSVPd(true);
        setExistingRSVP({ id: existingDoc.id, ...existingDoc.data() });
        showToast('RSVP updated!');
      } else {
        // ── Create new RSVP ────────────────────────────────────
        let phoneHash = hashedPhone;
        if (!phoneHash && currentPhoneE164) {
          try { phoneHash = (await resolvePhone(currentPhoneE164))?.token || null; } catch (e) {}
        }
        const newRSVP = await addDoc(collection(db, 'rsvps'), {
          eventId,
          response: responseVal,
          plusCount: parseInt(plusCount),
          name: submitName,
          deviceId,
          // See the note on the update path above — no PII on RSVP docs.
          ...(phoneHash ? { phoneToken: phoneHash } : {}),
          smsOptedIn: smsOptIn === true,
          timestamp: Timestamp.now()
        });
        await addDoc(collection(db, 'auditLogs'), {
          actorType: 'user',
          actorId: deviceId,
          actorName: submitName,
          action: 'rsvp_created',
          entityType: 'rsvp',
          entityId: newRSVP.id,
          entityLabel: formatEventLabel(selectedEvent),
          before: null,
          after: { response: responseVal, plusCount: parseInt(plusCount) },
          metadata: {
            source: 'web',
            phoneToken: phoneHash || null,
            deviceInfo: { fingerprint, sessionId, deviceId,
              ipAddress: deviceInfo.ipAddress, location: deviceInfo.location,
              userAgent: deviceInfo.userAgent, platform: deviceInfo.platform,
              screenResolution: deviceInfo.screenResolution, timezone: deviceInfo.timezone,
              language: deviceInfo.language, referrer: deviceInfo.referrer,
              connection: deviceInfo.connection },
          },
          timestamp: Timestamp.now()
        });
        touchSubscriberRsvp(currentPhoneE164);
        setHasRSVPd(true);
        setExistingRSVP({ id: newRSVP.id, eventId, response: responseVal,
          plusCount: parseInt(plusCount), name: submitName, deviceId });
        trackRsvp(responseVal);
        showToast('RSVP submitted!');
      }

      setResponse(responseVal);
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      showToast('Error submitting RSVP. Please try again.', 'error');
    } finally {
      setSubmitting(false);
      releaseLock();
    }
  };

  // ─── ADMIN RSVP ACTIONS ───────────────────────────────────────
  const handleAdminDeleteRsvp = async (rsvpId, rsvpData = {}) => {
    const who = rsvpData.name ? `${rsvpData.name}'s` : 'this';
    const what = rsvpData.response ? ` (${rsvpData.response})` : '';
    if (!window.confirm(`Delete ${who} RSVP${what}?`)) return;
    try {
      // The callable sees an authenticated admin and logs it as such.
      await callDeleteRsvp(rsvpId, { metadata: { source: 'admin-ui' } });
    } catch (error) { showToast('Error deleting RSVP', 'error'); }
  };

  // The IP no longer lives on the RSVP doc (world-readable — see firestore.rules),
  // so recover it from that RSVP's audit trail, which only admins can read.
  const lookupRsvpIp = async (rsvpId) => {
    try {
      const snap = await getDocs(query(collection(db, 'auditLogs'), where('entityId', '==', rsvpId)));
      for (const d of snap.docs) {
        const ip = d.data()?.metadata?.deviceInfo?.ipAddress;
        if (ip && ip !== 'unavailable') return ip;
      }
    } catch (e) { console.warn('IP lookup failed:', e); }
    return null;
  };

  const handleAdminBlockUser = async (rsvpId, userName) => {
    const ipAddress = await lookupRsvpIp(rsvpId);
    if (!ipAddress) {
      showToast('Cannot block: no IP address on record for this RSVP.', 'error');
      return;
    }
    if (!window.confirm(`Block "${userName}"? They will no longer be able to RSVP.`)) return;
    const daysStr = window.prompt('Block duration in days (leave blank for 30):', '30');
    if (daysStr === null) return; // admin cancelled the prompt
    const days = parseInt(daysStr) || 30;
    const expiresDate = new Date();
    expiresDate.setDate(expiresDate.getDate() + days);
    try {
      await setDoc(doc(db, 'blockedUsers', await banKey('ip', ipAddress)), {
        ipAddress,
        name: userName,
        blockedAt: Timestamp.now(),
        blockedBy: adminUser.email,
        expiresAt: Timestamp.fromDate(expiresDate)
      });
      await addDoc(collection(db, 'auditLogs'), {
        actorType: 'admin',
        actorId: adminUser.email,
        actorName: adminUser.email,
        action: 'user_blocked',
        entityType: 'user',
        entityId: ipAddress,
        entityLabel: userName,
        before: { blocked: false },
        after: { blocked: true, expiresAt: expiresDate.toISOString() },
        timestamp: Timestamp.now()
      });
      showToast(`Blocked "${userName}" for ${days} days`);
    } catch (error) { showToast('Error blocking user', 'error'); }
  };

  // ─── HELPERS ─────────────────────────────────────────────────
  const getTotal = (type) => rsvps
    .filter(r => r.response === type)
    .reduce((sum, r) => sum + 1 + (r.plusCount || 0), 0);

  // ─── SAVED INDICATOR ────────────────────────────────────────
  const flashSaved = () => {
    // Show spinning indicator briefly then snap back to checkmark
    setSavedIndicator(true);
    clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSavedIndicator(false), 800);
    showToast('Saved!');
  };

  // ─── SMS HELPERS ─────────────────────────────────────────────
  // Format a raw/E164/partial phone string as (xxx) xxx-xxxx for display.
  // Strips non-digits, ignores a leading 1, caps at 10 digits.
  const formatPhoneInput = (raw) => {
    let digits = (raw || '').replace(/\D/g, '');
    if (digits.startsWith('1') && digits.length === 11) digits = digits.slice(1);
    digits = digits.slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const toE164 = (raw) => {
    const digits = raw.replace(/\D/g, '');
    if (digits.startsWith('1') && digits.length === 11) return `+${digits}`;
    if (digits.length === 10) return `+1${digits}`;
    return '';
  };

  // Resolve a phone number to its opaque phoneToken plus subscription status.
  // The token is an HMAC keyed by a secret that never leaves Cloud Functions, so
  // it can only be obtained here — which is what stops the world-readable RSVP
  // collection from being brute-forced back into a list of phone numbers.
  const resolvePhone = async (rawPhone) => {
    const e164 = toE164(rawPhone);
    if (!e164) return null;
    if (smsTokenRef.current && smsTokenRef.current.phone === e164) return smsTokenRef.current.info;
    try {
      const res = await httpsCallable(getFunctions(), 'getSubscriptionStatus')({ phone: e164 });
      smsTokenRef.current = { phone: e164, info: res.data };
      return res.data;
    } catch (e) {
      console.warn('Subscription status lookup failed:', e);
      return null;
    }
  };

  const lookupSubscriber = async (rawPhone) => {
    const e164 = toE164(rawPhone);
    if (!e164) return;
    setSmsStatus('checking');
    try {
      // Server-side: the client cannot compute phoneToken (it is an HMAC keyed by
      // a Cloud Functions secret), and subscriber documents are no longer publicly
      // readable — that read handed out name and adminNotes to anyone with a
      // phone number. This returns only optedIn/smsStop plus the caller's token.
      const info = await resolvePhone(rawPhone);
      if (!info) { setSmsOptIn(false); setSmsStatus(null); return; }
      const key = info.token;

      if (info.exists) {
        if (info.optedIn) {
          setSmsOptIn(true);
          setSmsStatus('opted-in');
          // Persist phone locally so it restores on next page load
          localStorage.setItem('rsvp_sms_phone', rawPhone);
          localStorage.setItem('rsvp_sms_opted_in', '1');
        } else if (info.smsStop) {
          // Carrier-level block (user texted STOP) — must text START to re-subscribe,
          // cannot re-subscribe through the web UI
          setSmsOptIn(false);
          setSmsStatus('opted-out');
        } else {
          // App-level unsubscribe (unchecked the box) — show checkbox so they can
          // re-subscribe directly without having to text START
          setSmsOptIn(false);
          setSmsStatus(null);
        }
      } else {
        setSmsOptIn(false);
        setSmsStatus(null);
      }

      // ── RSVP dedup by phone token ────────────────────────────────
      // phoneToken is the only phone-derived key on an RSVP doc — the raw phone
      // was removed because these docs are world-readable, and the old plain hash
      // was brute-forceable back to the number. The Cloud Function stamps the same
      // token on SMS RSVPs, so one query finds both web and SMS duplicates.
      if (selectedEvent) {
        const byHashSnap = await getDocs(query(
          collection(db, 'rsvps'),
          where('eventId', '==', selectedEvent.id),
          where('phoneToken', '==', key)
        ));

        const allMatching = new Map();
        byHashSnap.docs.forEach(d => allMatching.set(d.id, d));

        if (allMatching.size > 0) {
          // Duplicates share this phoneToken, which is the ownership proof the
          // deleteRsvp callable checks; it writes the dedup audit entry too.
          const deleteDupe = (id) => callDeleteRsvp(id, {
            reason: 'dedup',
            phoneToken: key,
            metadata: { reason: 'phoneToken duplicate merge', source: 'web' },
          });

          if (existingRSVP) {
            // We already have an RSVP for this session. Make sure the survivor
            // carries the phoneToken before duplicates are deleted, so SMS
            // audience targeting keeps matching this player.
            if (!existingRSVP.phoneToken) {
              try {
                await updateDoc(doc(db, 'rsvps', existingRSVP.id), {
                  phoneToken: key,
                  updatedAt: Timestamp.now()
                });
                setExistingRSVP(prev => ({ ...prev, phoneToken: key }));
              } catch (e) { console.warn('[dedup] phoneToken copy failed:', e); }
            }
            // Delete every match that isn't the current RSVP — those are duplicates.
            let deleted = 0;
            for (const id of allMatching.keys()) {
              if (id !== existingRSVP.id) {
                try {
                  await deleteDupe(id);
                  deleted++;
                } catch (e) { console.warn('[dedup] delete failed for', id, e); }
              }
            }
            if (deleted > 0) showToast('Merged with your existing RSVP ✓');
          } else {
            // No RSVP for this session yet — pre-populate from the best match
            // (prefer the most-recently updated one).
            const sorted = [...allMatching.values()].sort((a, b) => {
              const ta = (a.data().updatedAt || a.data().timestamp)?.toMillis?.() ?? 0;
              const tb = (b.data().updatedAt || b.data().timestamp)?.toMillis?.() ?? 0;
              return tb - ta;
            });
            const best = { id: sorted[0].id, ...sorted[0].data() };
            setHasRSVPd(true);
            setExistingRSVP(best);
            setResponse(best.response);
            setPlusCount(best.plusCount || 0);
            // Do NOT reveal the name unless this browser already owns the RSVP.
            // Typing someone else's number used to turn it into their name — a
            // phone-to-identity oracle. Cross-device users see their RSVP restored
            // but type their name once; strangers learn nothing.
            if (best.name && best.deviceId && best.deviceId === getDeviceId()) setName(best.name);
            // Delete any extras beyond the best match
            for (const d of sorted.slice(1)) {
              try {
                await deleteDupe(d.id);
              } catch (e) {}
            }
          }
        } else {
        }
      } else {
      }
    } catch (e) {
      console.error('Subscriber lookup failed:', e);
      setSmsStatus(null);
    }
  };

  const handleSmsPhoneChange = (val) => {
    const formatted = formatPhoneInput(val);
    setSmsPhone(formatted);
    smsPhoneRef.current = formatted; // keep ref in sync synchronously for handleSubmit race safety
    setSmsOptIn(false);
    setSmsStatus(null);
    // Auto-lookup once we have a complete number
    const digits = val.replace(/\D/g, '');
    if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) {
      lookupSubscriber(val);
    }
  };

  const saveSubscriber = async (rawPhone, optIn) => {
    const e164 = toE164(rawPhone);
    if (!e164) return;
    const subscriberName = name.trim();
    const deviceId = getDeviceId();
    try {
      const key = (await resolvePhone(e164))?.token || null;
      // Written server-side: subscriber docs are no longer writable by
      // unauthenticated clients, because the old rule let anyone who knew a phone
      // number flip that person's optedIn/smsStop. The callable also writes the
      // audit entry and refuses to lift a carrier-level opt-out.
      await httpsCallable(getFunctions(), 'updateSubscription')({
        phone: e164,
        name: subscriberName,
        action: optIn ? 'subscribe' : 'unsubscribe',
      });
      localStorage.setItem('rsvp_sms_phone', rawPhone);
      localStorage.setItem('rsvp_sms_opted_in', optIn ? '1' : '0');

      // When phone is saved, store phoneHash on the current RSVP and
      // merge any duplicate RSVPs for this event with the same phone.
      // `existingRSVP` may not be set yet if this fires while the original
      // RSVP submission is still in flight (race: user opts in to texts
      // within a second of tapping Yes) — fall back to a deviceId lookup
      // so the phone doesn't silently fail to attach to the RSVP.
      let targetRSVP = existingRSVP;
      if (selectedEvent && !targetRSVP) {
        try {
          const ownSnap = await getDocs(query(
            collection(db, 'rsvps'),
            where('eventId', '==', selectedEvent.id),
            where('deviceId', '==', deviceId)
          ));
          if (!ownSnap.empty) {
            targetRSVP = { id: ownSnap.docs[0].id, ...ownSnap.docs[0].data() };
            setExistingRSVP(targetRSVP);
            setHasRSVPd(true);
          }
        } catch (e) { console.warn('Fallback RSVP lookup by deviceId failed:', e); }
      }

      if (selectedEvent && targetRSVP && key) {
        // Update current RSVP with the phone token
        try {
          // phoneToken only — never the raw number, and never a plain hash
          // (RSVP docs are world-readable and a plain hash is brute-forceable).
          // smsOptedIn drives the dot and follows the checkbox immediately.
          await updateDoc(doc(db, 'rsvps', targetRSVP.id), {
            phoneToken: key, smsOptedIn: optIn === true, updatedAt: Timestamp.now()
          });
          await addDoc(collection(db, 'auditLogs'), {
            actorType: 'user',
            actorId: deviceId,
            actorName: subscriberName || null,
            action: 'rsvp_updated',
            entityType: 'rsvp',
            entityId: targetRSVP.id,
            entityLabel: formatEventLabel(selectedEvent),
            before: { phoneToken: targetRSVP.phoneToken || null },
            after: { phoneToken: key },
            metadata: { reason: 'sms opt-in phone backfill', source: 'web' },
            timestamp: Timestamp.now()
          });
        } catch (e) { console.warn('Could not update RSVP with phoneToken:', e); }

        // Find and delete any other RSVPs for this event with the same token
        try {
          const dupeSnap = await getDocs(query(
            collection(db, 'rsvps'),
            where('eventId', '==', selectedEvent.id),
            where('phoneToken', '==', key)
          ));
          for (const d of dupeSnap.docs) {
            if (d.id !== targetRSVP.id) {
              await callDeleteRsvp(d.id, {
                reason: 'dedup',
                phoneToken: key,
                metadata: { reason: 'phoneToken duplicate merge on subscribe', source: 'web' },
              });
            }
          }
        } catch (e) { console.warn('Duplicate RSVP merge failed:', e); }
      }
    } catch (e) {
      console.error('Error saving subscriber:', e);
    }
  };

  // ─── RENDER SELECTED EVENT ────────────────────────────────────
  const renderEventCard = () => {
    if (!selectedEvent) return (
      <div className="cal-event-preview cal-no-event">
        <span className="cal-no-event-text">Select a date to view event details</span>
      </div>
    );

    const now = new Date();
    const eventDate = selectedEvent.date.toDate();
    // Use endTime to determine when event is over — not start time.
    // This prevents the card from switching to "past" while the game is still running.
    // If endTime is unset, getEventEndTime defaults to start + 3 hours.
    const eventEnd = getEventEndTime(selectedEvent);
    const isEventPast = eventEnd < now;

    // Lock RSVPs until X hours before the event
    const lockHours = selectedEvent.lockRsvpHours ?? 48;
    const lockedMessage = selectedEvent.lockedRsvpMessage || '(when weather forecast is reliable)';
    const msUntilLock = eventDate - now - (lockHours * 60 * 60 * 1000);
    const isLocked = lockHours > 0 && !isEventPast && msUntilLock > 0;
    const min = minPlayers || 8;

    // Count yes attendees including guests
    const yesTotal = getTotal('yes');
    const needed = Math.max(0, min - yesTotal);

    // Time until event
    const msUntil = eventDate - now;
    const daysUntil = Math.floor(msUntil / (1000 * 60 * 60 * 24));
    const hoursUntil = Math.floor((msUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const totalHoursUntil = msUntil / (1000 * 60 * 60);

    // "Decision pending" window — 2 hours before kickoff is when we decide
    // whether to cancel for lack of players. The message should only
    // surface starting 15 hours out (same time the reminder text goes out),
    // and only while still short of the minimum — most games never need this.
    const decisionDeadline = new Date(eventDate.getTime() - 2 * 60 * 60 * 1000);
    const decisionDeadlineStr = decisionDeadline.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit', hour12: true
    });
    const isDecisionPending = totalHoursUntil > 0 && totalHoursUntil <= 15;

    // [90minBeforeEvent] — same idea as [2HoursBeforeEvent], 90 minutes out.
    const ninetyMinBefore = new Date(eventDate.getTime() - 90 * 60 * 1000);
    const ninetyMinBeforeStr = ninetyMinBefore.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit', hour12: true
    });

    // Banner state for upcoming events (not past, not manually confirmed/canceled)

    // Progress bar fill

    // ── Resolve custom status message ──
    const unlockAtGlobal = new Date(eventDate.getTime() - lockHours * 60 * 60 * 1000);
    const unlockDateTimeStr = unlockAtGlobal.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });

    const resolveStatusMsg = (template) => {
      if (!template) return '';
      return template
        .replace(/\[CurrentPlayerCount\]/gi, String(yesTotal))
        .replace(/\[PlayersNeeded\]/gi, String(needed))
        .replace(/\[ActualAttendance\]/gi, selectedEvent.actualAttendance != null ? String(selectedEvent.actualAttendance) : '(not recorded)')
        .replace(/\[DateTimeThisGameUnlocks\]/gi, unlockDateTimeStr)
        .replace(/\[2HoursBeforeEvent\]/gi, decisionDeadlineStr)
        .replace(/\[90minBeforeEvent\]/gi, ninetyMinBeforeStr);
    };

    // Find a row by type or by player count range; returns {message, color} or null
    const getSpecialRow = (type) => {
      if (!statusLines || statusLines.length === 0) return null;
      return statusLines.find(row => row.type === type) || null;
    };
    const getCustomStatusMsg = () => {
      if (!statusLines || statusLines.length === 0) return null;
      const match = [...statusLines.filter(r => !r.type)]
        .sort((a, b) => (b.minCount || 0) - (a.minCount || 0))
        .find(row => yesTotal >= (row.minCount || 0) && yesTotal <= (row.maxCount ?? 999));
      return match ? { msg: resolveStatusMsg(match.message), color: match.color || null } : null;
    };

    // ── State band config ──
    const bandState = (() => {
      // flagText is always current player count
      // flagLabel conveys the state meaning
      if (selectedEvent.isCanceled) {
        const canceledRow = getSpecialRow('canceled');
        return {
          bg: 'var(--band-canceled)', segColor: '#ef4444',
          flagBg: (canceledRow && canceledRow.color) || '#dc2626',
          flagText: String(yesTotal), flagLabel: 'canceled',
          statusMsg: canceledRow ? resolveStatusMsg(canceledRow.message) : 'This game has been canceled.',
        };
      }
      if (selectedEvent.isConfirmed) {
        const confirmedRow = getSpecialRow('confirmed');
        const custom = getCustomStatusMsg();
        return {
          bg: 'var(--band-on)', segColor: '#4ade80',
          flagBg: (confirmedRow && confirmedRow.color) || (custom && custom.color) || '#16a34a',
          flagText: String(yesTotal), flagLabel: '✓ game on',
          statusMsg: confirmedRow ? resolveStatusMsg(confirmedRow.message) : (custom && custom.msg) || `Game confirmed · ${daysUntil > 0 ? `${daysUntil}d ${hoursUntil}h` : `${hoursUntil}h`} away`,
        };
      }
      if (isEventPast) {
        const pastRow = getSpecialRow('past');
        const actualCount = selectedEvent.actualAttendance != null
          ? String(selectedEvent.actualAttendance)
          : String(yesTotal);
        return {
          bg: 'var(--band-past)', segColor: '#60a5fa',
          flagBg: (pastRow && pastRow.color) || '#4b5563',
          flagText: actualCount,
          flagLabel: 'attended',
          statusMsg: pastRow ? resolveStatusMsg(pastRow.message) : null,
        };
      }
      if (isLocked) {
        const lockedRow = getSpecialRow('locked');
        return {
          bg: 'var(--band-default)', segColor: 'rgba(255,255,255,.2)',
          flagBg: (lockedRow && lockedRow.color) || '#6d28d9',
          flagText: String(yesTotal), flagLabel: 'Locked',
          statusMsg: null,
          lockedCardMsg: lockedRow ? resolveStatusMsg(lockedRow.message) : null,
        };
      }
      const decisionRowConfig = getSpecialRow('decision_pending');
      if (isDecisionPending && needed > 0 && decisionRowConfig?.enabled !== false) {
        const decisionRow = decisionRowConfig;
        const custom = getCustomStatusMsg();
        return {
          bg: 'var(--band-close)', segColor: '#fbbf24',
          flagBg: (decisionRow && decisionRow.color) || (custom && custom.color) || '#b45309',
          flagText: String(yesTotal), flagLabel: `Need min. ${needed} more`,
          statusMsg: decisionRow
            ? resolveStatusMsg(decisionRow.message)
            : `${yesTotal} in, ${needed} more needed — we'll decide by ${decisionDeadlineStr}. We'll text everyone either way.`,
        };
      }
      if (yesTotal === 0) {
        const custom = getCustomStatusMsg();
        return {
          bg: 'var(--band-past)', segColor: 'rgba(255,255,255,.2)',
          flagBg: (custom && custom.color) || '#374151',
          flagText: '0', flagLabel: `Need min. ${min} more`,
          statusMsg: (custom && custom.msg) || `Be the first to join — we need ${min} to play.`,
        };
      }
      if (needed === 0) {
        const custom = getCustomStatusMsg();
        return {
          bg: 'var(--band-on)', segColor: '#4ade80',
          flagBg: (custom && custom.color) || '#16a34a',
          flagText: String(yesTotal), flagLabel: '✓ game on',
          statusMsg: (custom && custom.msg) || 'Game is ON. See you out there!',
        };
      }
      if (needed <= 2) {
        const custom = getCustomStatusMsg();
        return {
          bg: 'var(--band-close)', segColor: '#fbbf24',
          flagBg: (custom && custom.color) || '#d97706',
          flagText: String(yesTotal), flagLabel: `Need min. ${needed} more`,
          statusMsg: (custom && custom.msg) || 'So close — just a couple more players needed.',
        };
      }
      if (yesTotal >= Math.floor(min / 2)) {
        const custom = getCustomStatusMsg();
        return {
          bg: 'var(--band-default)', segColor: '#60a5fa',
          flagBg: (custom && custom.color) || '#2563eb',
          flagText: String(yesTotal), flagLabel: `Need min. ${needed} more`,
          statusMsg: (custom && custom.msg) || 'Halfway there — keep it going!',
        };
      }
      const custom = getCustomStatusMsg();
      return {
        bg: 'var(--band-default)', segColor: '#60a5fa',
        flagBg: (custom && custom.color) || '#1d4ed8',
        flagText: String(yesTotal), flagLabel: `Need min. ${needed} more`,
        statusMsg: (custom && custom.msg) || 'Getting started. Help spread the word!',
      };
    })();

    // Weather widget ref
    const loc = locations[selectedEvent.primaryLocationId];
    const daysToEvent = (eventDate - now) / (1000 * 60 * 60 * 24);
    const showWeatherWidget = loc && loc.lat && loc.lng && daysToEvent >= 0 && daysToEvent <= 7 && showWeather !== false;

    // Location name + popup handler
    const locationName = loc ? loc.name : null;
    const openLocationPopup = () => {
      const locs = [];
      ['primaryLocationId', 'backup1LocationId', 'backup2LocationId'].forEach((key, i) => {
        const id = selectedEvent[key];
        if (id && locations[id]) locs.push({ ...locations[id], role: ['Primary', 'Backup 1', 'Backup 2'][i] });
      });
      setPopupLocations(locs);
      setShowLocationPopup(true);
    };

    // Description preview (first 50 chars of plain text)
    const rawDesc = selectedEvent.description ? selectedEvent.description.replace(/<[^>]+>/g, '') : '';
    const DESC_PREVIEW = 50;
    const hasLongDesc = rawDesc.length > DESC_PREVIEW;

    return (
      <div className="cal-event-preview">
        <div className={`event-card evt-new-card${pulseCard ? ' evt-card-pulse' : ''}`}>

          {/* Admin toolbar */}
          <AdminToolbar
            event={selectedEvent}
            adminUser={adminUser}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onSaveAttendance={onSaveAttendance}
          />

          {/* ── STATE BAND ── */}
          <div className="evt-state-band" style={{background: bandState.bg}}>

            <div className="evt-band-top">
              {/* Left: date/time/location */}
              <div className="evt-band-left">
{selectedEvent.id === nextEventId && !isEventPast && <div className="evt-band-eyebrow">Next game</div>}
                <div className="evt-band-date">{format(eventDate, 'EEEE, MMM d')}</div>
                <div className="evt-band-meta">
                  <span>{format(eventDate, 'h:mm a')}</span>
                  {showWeatherWidget && (
                    <button className="evt-weather-btn" onClick={() => {
                      trackWeatherClicked();
                      // Windy requires YYYY-MM-DD-HH format in UTC,
                      // where HH must be a 3-hour interval: 00,03,06,09,12,15,18,21
                      const d = new Date(eventDate);
                      const hh = Math.floor(d.getUTCHours() / 3) * 3;
                      const pad = n => String(n).padStart(2, '0');
                      const timeStr = `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())}-${pad(hh)}`;
                      const url = `https://www.windy.com/?wind,${timeStr},${loc.lat},${loc.lng},17`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}>
                      <WeatherWidget eventDate={eventDate} lat={loc.lat} lng={loc.lng} inline={true} />
                      <span className="evt-weather-chevron">›</span>
                    </button>
                  )}
                </div>
                <div className="evt-band-location">
                  {locationName ? (
                    <button className="evt-location-btn" onClick={openLocationPopup}>
                      {locationName}
                    </button>
                  ) : (
                    <span className="evt-location-tbd">Location TBD</span>
                  )}
                </div>
              </div>

              {/* Right: unified flag chip — always visible when flagBg set */}
              {bandState.flagBg && (
                <div className="evt-flag-chip" style={{background: bandState.flagBg}}>
                  {bandState.statusMsg && !isEventPast && (
                    <div className="evt-flag-status" dangerouslySetInnerHTML={{__html: bandState.statusMsg}} />
                  )}
                  <div className="evt-flag-body">
                    <div className="evt-flag-num">
                      {isLocked && !isEventPast ? '🔒' : bandState.flagText}
                    </div>
                    {!isLocked && !isEventPast && (() => {
                      const maybeTotal = rsvps
                        .filter(r => r.response === 'maybe')
                        .reduce((s, r) => s + 1 + (r.plusCount || 0), 0);
                      return (
                        <div className="evt-flag-text-col">
                          <div className="evt-flag-text-bot" style={{fontSize:'11px'}}>Going</div>
                          {maybeTotal > 0 && (
                            <div style={{
                              fontSize: '9px', fontWeight: 600, lineHeight: 1.2,
                              color: '#f5c842', letterSpacing: 0, marginTop: '2px',
                            }}>
                              +{maybeTotal} maybe
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {isEventPast && (
                      <div className="evt-flag-text-col">
                        <div className="evt-flag-text-bot">{selectedEvent.isCanceled ? 'Canceled' : 'Attended'}</div>
                      </div>
                    )}
                  </div>
                  {!isEventPast && (
                    <div className="evt-flag-label">{bandState.flagLabel}</div>
                  )}
                </div>
              )}
            </div>

            {/* Segment bar */}
            {!isEventPast && !selectedEvent.isCanceled && (
              <div className="evt-seg-bar">
                {Array.from({length: min}).map((_, i) => (
                  <div key={i} className="evt-seg" style={{background: i < yesTotal ? bandState.segColor : 'rgba(255,255,255,.15)'}} />
                ))}
              </div>
            )}

            {/* Name pills + overflow — future events */}
            {!isEventPast && yesTotal > 0 && (
              <div className="evt-band-pills">
                {rsvps.filter(r => r.response === 'yes').slice(0, 4).map((r, i) => (
                  <span key={i} className="evt-band-pill" style={{display:'inline-flex', alignItems:'center', gap:'3px'}}>
                    {(() => { const p = (r.name||'').trim().split(/\s+/); return p.length > 1 ? `${p[0]} ${p[p.length-1][0]}.` : p[0]; })()}
                    {(r.plusCount > 0) && (
                      <span style={{fontSize:'9px', opacity:0.7}}>+{r.plusCount}</span>
                    )}
                    {adminUser && (
                      <button
                        onClick={e => { e.stopPropagation(); handleAdminDeleteRsvp(r.id, r); }}
                        style={{fontSize:'9px', lineHeight:1, background:'rgba(255,255,255,.2)',
                          border:'none', borderRadius:'50%', color:'rgba(255,255,255,.7)',
                          cursor:'pointer', padding:'1px 3px', marginLeft:'1px'}}
                        title={`Remove ${r.name}`}>✕</button>
                    )}
                  </span>
                ))}
                {yesTotal > 4 && (
                  <button className="evt-band-overflow" onClick={() => setWhosComingOpen(p => !p)}>
                    +{yesTotal - 4} more {whosComingOpen ? '▴' : '▾'}
                  </button>
                )}
              </div>
            )}

            {/* Name pills + overflow — past events */}
            {isEventPast && yesTotal > 0 && (
              <div className="evt-band-pills">
                {rsvps.filter(r => r.response === 'yes').slice(0, 4).map((r, i) => (
                  <span key={i} className="evt-band-pill" style={{display:'inline-flex', alignItems:'center', gap:'3px'}}>
                    {(() => { const p = (r.name||'').trim().split(/\s+/); return p.length > 1 ? `${p[0]} ${p[p.length-1][0]}.` : p[0]; })()}
                    {(r.plusCount > 0) && (
                      <span style={{fontSize:'9px', opacity:0.7}}>+{r.plusCount}</span>
                    )}
                  </span>
                ))}
                <button className="evt-band-overflow" onClick={() => setWhosComingOpen(p => !p)}>
                  {yesTotal > 4 ? `+${yesTotal - 4} more ${whosComingOpen ? '▴' : '▾'}` : (whosComingOpen ? 'less ▴' : 'all ▾')}
                </button>
              </div>
            )}

          </div>{/* end evt-state-band */}
          <div className="evt-lockable">

          {/* Lock card — in normal flow so accordions can expand freely */}
          {isLocked && !isEventPast && (() => {
            const unlockAt = new Date(eventDate.getTime() - lockHours * 60 * 60 * 1000);
            const unlockDay = unlockAt.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'long' });
            const unlockDate = unlockAt.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'long', day: 'numeric' });
            const unlockTime = unlockAt.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit', hour12: true });

            const toggleAccordion = (key) =>
              setLockAccordion(prev => prev === key ? null : key);

            const accordionStyle = (key) => ({
              display: 'grid',
              gridTemplateRows: lockAccordion === key ? '1fr' : '0fr',
              transition: 'grid-template-rows 260ms ease',
              overflow: 'hidden',
              width: '100%',
            });

            return (
              <div style={{
                background: 'var(--band-default)',
                display:'flex', flexDirection:'column', alignItems:'center',
                padding:'1.5rem 1.25rem 1.25rem', borderRadius:0,
              }}>

                {/* Main message */}
                <div style={{textAlign:'center', marginBottom:'1.1rem'}}>
                  <div style={{fontSize:'13px', color:'rgba(255,255,255,.5)', marginBottom:'4px', letterSpacing:'.02em'}}>
                    RSVP opens
                  </div>
                  <div style={{fontSize:'17px', fontWeight:600, color:'white', lineHeight:1.25}}>
                    {unlockDay}, {unlockDate}
                  </div>
                  <div style={{fontSize:'15px', color:'rgba(255,255,255,.75)', marginTop:'2px'}}>
                    {unlockTime}
                  </div>
                  <div style={{fontSize:'12px', color:'rgba(255,255,255,.35)', marginTop:'6px', fontStyle:'italic'}}>
                    {lockedMessage ? (
                      <span dangerouslySetInnerHTML={{__html: lockedMessage}} />
                    ) : (
                      '(when weather forecast is reliable)'
                    )}
                  </div>
                </div>

                {/* Accordion buttons */}
                <div style={{width:'100%', maxWidth:'280px', display:'flex', flexDirection:'column', gap:'6px'}}>

                  {/* Get text reminders */}
                  <div>
                    <button
                      onClick={() => toggleAccordion('sms')}
                      style={{
                        width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
                        padding:'.55rem .85rem', background:'rgba(255,255,255,.1)',
                        border:'1px solid rgba(255,255,255,.18)', borderRadius: lockAccordion === 'sms' ? '6px 6px 0 0' : '6px',
                        color:'white', fontSize:'14px', cursor:'pointer', fontFamily:'inherit',
                        transition:'border-radius .26s',
                      }}>
                      {/* "Get" dropped to make room — the chevron already signals it opens */}
                      <span>📱 Text reminders</span>
                      <span style={{display:'flex', alignItems:'center', gap:'7px'}}>
                        {/* Shortened deliberately. This column is capped at 280px and is
                            narrower still on a 320px phone; "N players subscribed" wraps
                            there and grows the button from 39px to 58px. The unlocked
                            panel's row is wider and carries the full wording. */}
                        {showJoinedPill && <span style={joinedPillStyle}>{subscriberCount} subscribed</span>}
                        <span style={{fontSize:'11px', opacity:.6}}>{lockAccordion === 'sms' ? '▴' : '▾'}</span>
                      </span>
                    </button>
                    <div style={accordionStyle('sms')}>
                      <div style={{minHeight:0, background:'rgba(255,255,255,.07)',
                        border:'1px solid rgba(255,255,255,.18)', borderTop:'none',
                        borderRadius:'0 0 6px 6px', padding: lockAccordion === 'sms' ? '.75rem' : '0 .75rem'}}>
                        <div style={{fontSize:'12px', color:'rgba(255,255,255,.6)', marginBottom:'.6rem'}}>
                          {showJoinedPill
                            ? `${subscriberCount} players get game-day texts. We'll remind you before each game.`
                            : "We'll text you when RSVPs open and remind you before the game."}
                        </div>
                        <div style={{marginBottom:'.5rem'}}>
                          <label style={{display:'block', fontSize:'11px', color:'rgba(255,255,255,.5)',
                            marginBottom:'3px', textTransform:'uppercase', letterSpacing:'.05em'}}>
                            Your name <span style={{color:'#f87171'}}>*</span>
                          </label>
                          <input
                            type="text"
                            className="rsvp-name-input"
                            value={name}
                            onChange={e => {
                              const val = e.target.value;
                              setName(val);
                              if (val.trim()) localStorage.setItem('rsvp_user_name', val.trim());
                            }}
                            placeholder="Required"
                            required
                            style={{width:'100%'}}
                          />
                        </div>
                        <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                          <input
                            type="tel"
                            className="sms-optin-input"
                            value={smsPhone}
                            onChange={e => handleSmsPhoneChange(e.target.value)}
                            placeholder="Optional phone number"
                            style={{width:'150px', flexShrink:0}}
                          />
                          {smsStatus === 'checking' && <span style={{fontSize:'13px', color:'rgba(255,255,255,.5)'}}>⟳</span>}
                          {smsPhone.length > 0 && smsStatus === 'opted-in' && <span style={{color:'#4ade80', fontSize:'14px', fontWeight:700}}>✓</span>}
                        </div>
                        {smsPhone.length > 0 && smsStatus === 'opted-out' && (
                          <div style={{fontSize:'11px', color:'#f87171', marginTop:'.3rem'}}>
                            This number has unsubscribed. Text START to (888) 968-4999 to re-subscribe.
                          </div>
                        )}
                        {smsPhone.length > 0 && smsStatus !== 'opted-out' && smsStatus !== 'checking' && (
                          <label style={{display:'flex', alignItems:'center', gap:'6px', marginTop:'.4rem',
                            fontSize:'12px', color:'rgba(255,255,255,.7)', cursor:'pointer'}}>
                            <input type="checkbox" checked={smsOptIn}
                              onChange={e => {
                                if (!name.trim()) { showToast('Please enter your name first', 'error'); return; }
                                setSmsOptIn(e.target.checked);
                                if (smsPhone) { saveSubscriber(smsPhone, e.target.checked); showToast('Subscription updated!'); }
                              }} />
                            {SITE.smsOptInLabel}
                          </label>
                        )}
                        <div style={{fontSize:'10px', color:'rgba(255,255,255,.3)', marginTop:'.4rem', lineHeight:1.4}}>
                          {SMS_DISCLOSURE}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Add to calendar */}
                  <div>
                    <button
                      onClick={() => toggleAccordion('calendar')}
                      style={{
                        width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
                        padding:'.55rem .85rem', background:'rgba(255,255,255,.1)',
                        border:'1px solid rgba(255,255,255,.18)', borderRadius: lockAccordion === 'calendar' ? '6px 6px 0 0' : '6px',
                        color:'white', fontSize:'14px', cursor:'pointer', fontFamily:'inherit',
                        transition:'border-radius .26s',
                      }}>
                      <span>📅 Add to calendar</span>
                      <span style={{fontSize:'11px', opacity:.6}}>{lockAccordion === 'calendar' ? '▴' : '▾'}</span>
                    </button>
                    <div style={accordionStyle('calendar')}>
                      <div style={{minHeight:0, background:'rgba(255,255,255,.07)',
                        border:'1px solid rgba(255,255,255,.18)', borderTop:'none',
                        borderRadius:'0 0 6px 6px', padding: lockAccordion === 'calendar' ? '.75rem' : '0 .75rem'}}>
                        <CalendarButtons
                          selectedEvent={selectedEvent}
                          location={selectedEvent ? locations[selectedEvent.primaryLocationId] : null}
                          recurringSettings={recurringSettings}
                          defaultLocation={selectedEvent ? locations[selectedEvent.primaryLocationId] : null}
                          siteUrl={siteUrl}
                          calendarDescription={calendarDescription}
                          showSingle={true}
                          showSeries={true}
                          dark={true}
                          showToast={showToast}
                        />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            );
          })()}



          {/* ── NORMAL CONTENT — hidden when locked ── */}
          {(!isLocked || isEventPast || selectedEvent.isCanceled) && <>

          {/* ── WHO'S COMING / WHO CAME expanded list (shared for future + past) ── */}
          {whosComingOpen && (
            <div className="evt-whos-coming">
              {(() => {
                const yes = rsvps.filter(r => r.response === 'yes');
                const maybe = rsvps.filter(r => r.response === 'maybe');
                const no = rsvps.filter(r => r.response === 'no');
                return (
                  <>
                    {yes.length > 0 && (
                      <div className="evt-whos-group">
                        <div className="evt-whos-label">{isEventPast ? `Attended (${yes.reduce((s, r) => s + 1 + (r.plusCount || 0), 0)})` : `In (${yes.reduce((s, r) => s + 1 + (r.plusCount || 0), 0)})`}</div>
                        {yes.map(r => (
                          <div key={r.id} className="evt-whos-row">
                            {smsMark(r.smsOptedIn)}
                            <span className="evt-whos-name">{r.name}{r.plusCount > 0 ? <span className="evt-whos-guests-inline"> +{r.plusCount}</span> : ''}</span>
                            {!isEventPast && adminUser && (
                              <span className="admin-rsvp-actions">
                                <button className="admin-rsvp-btn delete" onClick={() => handleAdminDeleteRsvp(r.id, r)}>✕</button>
                                <button className="admin-rsvp-btn block" onClick={() => handleAdminBlockUser(r.id, r.name)}>⊘</button>
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {maybe.length > 0 && (
                      <div className="evt-whos-group">
                        <div className="evt-whos-label">{isEventPast ? `Maybe (${maybe.length})` : `Maybe (${maybe.reduce((s, r) => s + 1 + (r.plusCount || 0), 0)})`}</div>
                        {maybe.map(r => (
                          <div key={r.id} className="evt-whos-row evt-whos-maybe">
                            {smsMark(r.smsOptedIn)}
                            <span className="evt-whos-name">{r.name}{r.plusCount > 0 ? <span className="evt-whos-guests-inline"> +{r.plusCount}</span> : ''}</span>
                            {!isEventPast && adminUser && (
                              <span className="admin-rsvp-actions">
                                <button className="admin-rsvp-btn delete" onClick={() => handleAdminDeleteRsvp(r.id, r)}>✕</button>
                                <button className="admin-rsvp-btn block" onClick={() => handleAdminBlockUser(r.id, r.name)}>⊘</button>
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {no.length > 0 && (
                      <div className="evt-whos-group">
                        <div className="evt-whos-label">{isEventPast ? `Couldn't make it (${no.length})` : `Out (${no.length})`}</div>
                        {no.map(r => (
                          <div key={r.id} className="evt-whos-row evt-whos-no">
                            {smsMark(r.smsOptedIn)}
                            <span className="evt-whos-name">{r.name}</span>
                            {!isEventPast && adminUser && (
                              <span className="admin-rsvp-actions">
                                <button className="admin-rsvp-btn delete" onClick={() => handleAdminDeleteRsvp(r.id, r)}>✕</button>
                                <button className="admin-rsvp-btn block" onClick={() => handleAdminBlockUser(r.id, r.name)}>⊘</button>
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Recruiting copy, not just a key: the marker says who,
                        this line is what actually invites people to join. */}
                    {rsvps.some(r => r.smsOptedIn) && (
                      <div style={{fontSize:'10px', color:'rgba(255,255,255,.45)', padding:'6px 0.75rem 2px', borderTop:'0.5px solid rgba(255,255,255,.08)', marginTop:'4px', display:'flex', alignItems:'center', gap:'6px'}}>
                        {/* Dimmed so the key doesn't compete with the live marks above it.
                            0.8, not 0.55: the teal composites toward the card colour as
                            opacity drops, and below ~0.7 the swatch goes muddy. */}
                        {smsMarkGlyph({opacity: 0.8})}
                        <span>Gets game texts — you can RSVP by text, too.</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* ── DESCRIPTION PREVIEW ── */}
          {rawDesc && (
            <div className="evt-desc-preview">
              {descExpanded ? (
                <>
                  <span dangerouslySetInnerHTML={{ __html: selectedEvent.description }} />
                  {' '}<button className="evt-desc-toggle" onClick={() => setDescExpanded(false)}>Less ▴</button>
                </>
              ) : (
                <>
                  <span>{rawDesc.slice(0, DESC_PREVIEW)}{hasLongDesc ? '…' : ''}</span>
                  {hasLongDesc && <button className="evt-desc-toggle" onClick={() => setDescExpanded(true)}> More</button>}
                </>
              )}
            </div>
          )}

          {/* ── RSVP FORM ── */}
          {!selectedEvent.isCanceled ? (
            <div className="rsvp-callout">
              {!isEventPast && (
                <>
                  {/* Response buttons */}
                  <div className="rsvp-btn-row">
                    <button
                      type="button"
                      disabled={submitting}
                      className={[
                        'rsvp-btn-yes',
                        response === 'yes' ? 'selected' : '',
                        (response === 'maybe' || response === 'no') ? 'other-selected' : ''
                      ].filter(Boolean).join(' ')}
                      onClick={() => handleSubmit('yes')}
                    >
                      {submitting && response === 'yes'
                        ? '...'
                        : response === 'yes'
                          ? "✓ I'm Playing"
                          : "I'm Playing"}
                    </button>
                    <button
                      type="button"
                      disabled={submitting}
                      className={`rsvp-btn-secondary rsvp-btn-maybe${response === 'maybe' ? ' selected' : ''}`}
                      onClick={() => handleSubmit('maybe')}
                    >
                      {submitting && response === 'maybe' ? '...' : response === 'maybe' ? '✓ Maybe' : 'Maybe'}
                    </button>
                    <button
                      type="button"
                      disabled={submitting}
                      className={`rsvp-btn-secondary rsvp-btn-no${response === 'no' ? ' selected' : ''}`}
                      onClick={() => handleSubmit('no')}
                    >
                      {submitting && response === 'no' ? '...' : response === 'no' ? '✓ No' : 'No'}
                    </button>
                  </div>

                  {/* Disclaimer below buttons */}
                  {disclaimer && (
                    <div className="evt-disclaimer"
                      dangerouslySetInnerHTML={{ __html: disclaimer }} />
                  )}

                  {/* ── POST-RSVP EXPANSION PANEL (Yes or Maybe only) ── */}
                  <div className={`rsvp-expansion${(response === 'yes' || response === 'maybe' || response === 'no') ? ' rsvp-expansion-open' : ''}`}>
                    <div className="rsvp-expansion-inner">

                      {/* Confirmation message */}
                      <div className="rsvp-expansion-confirm">
                        {response === 'yes'
                          ? (name.trim() ? `👍 You're in, ${name.trim()}!` : "👍 You're in — add your name")
                          : response === 'maybe'
                          ? (name.trim() ? `🤙 Got it, ${name.trim()} — you're a Maybe` : "🤙 You're thinking about it — add your name")
                          : (name.trim() ? `👋 Got it, ${name.trim()} — you're marked Out` : "👋 Almost done — add your name")}
                      </div>

                      {/* Name, SMS, calendar — all responses */}
                      {(response === 'yes' || response === 'maybe' || response === 'no') && (<>

                      {/* Name + guests */}
                      <div className="rsvp-expansion-section">
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.3rem'}}>
                          <div style={{fontSize:'12px', color:'rgba(255,255,255,.5)'}}>
                            Your name <span style={{color:'rgba(255,255,255,.3)'}}>— {response === 'no' ? "so your teammates know you're out" : "so others know who's coming"}</span>
                          </div>
                        </div>
                        <div style={{display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'nowrap'}}>
                          <div style={{flex:1, minWidth:0}}>
                            <input
                              ref={nameRef}
                              type="text"
                              className="rsvp-name-input"
                              value={name}
                              onChange={e => {
                                const val = e.target.value;
                                setName(val);
                                clearTimeout(nameTimerRef.current);
                                nameTimerRef.current = setTimeout(async () => {
                                  if (val.trim() && hasRSVPd && existingRSVP) {
                                    localStorage.setItem('rsvp_user_name', val.trim());
                                    const prevName = existingRSVP.name || null;
                                    // Update RSVP
                                    try {
                                      await updateDoc(doc(db, 'rsvps', existingRSVP.id), { name: val.trim(), updatedAt: Timestamp.now() });
                                      await addDoc(collection(db, 'auditLogs'), {
                                        actorType: 'user',
                                        actorId: getDeviceId(),
                                        actorName: val.trim(),
                                        action: 'rsvp_updated',
                                        entityType: 'rsvp',
                                        entityId: existingRSVP.id,
                                        entityLabel: formatEventLabel(selectedEvent),
                                        before: { name: prevName },
                                        after: { name: val.trim() },
                                        metadata: { reason: 'name edit', source: 'web' },
                                        timestamp: Timestamp.now()
                                      });
                                    } catch (e) { console.error('Name update failed:', e); }
                                    // Keep the subscriber's display name in step. Subscriber docs
                                    // are neither readable nor writable by visitors any more (the
                                    // public read handed out name + adminNotes to anyone with a
                                    // phone number), so this goes through the callable, which
                                    // writes the audit entry itself.
                                    const phoneForSub = smsPhone || localStorage.getItem('rsvp_sms_phone') || '';
                                    if (phoneForSub && toE164(phoneForSub)) {
                                      try {
                                        await httpsCallable(getFunctions(), 'updateSubscription')({
                                          phone: toE164(phoneForSub),
                                          name: val.trim(),
                                          action: 'setName',
                                        });
                                      } catch (e) { console.error('Subscriber name update failed:', e); }
                                    }
                                    flashSaved();
                                  } else if (val.trim()) {
                                    localStorage.setItem('rsvp_user_name', val.trim());
                                  }
                                }, 800);
                              }}
                              placeholder="Your name"
                              style={{width:'100%'}}
                            />
                          </div>
                          <div className="rsvp-guests-inline" style={{flexShrink:0}}>
                            <label>+</label>
                            <select
                              value={plusCount}
                              onChange={e => {
                                const val = e.target.value;
                                const prevPlusCount = existingRSVP?.plusCount ?? 0;
                                setPlusCount(val);
                                if (hasRSVPd && existingRSVP) {
                                  updateDoc(doc(db, 'rsvps', existingRSVP.id), { plusCount: parseInt(val), updatedAt: Timestamp.now() })
                                    .then(() => {
                                      flashSaved();
                                      return addDoc(collection(db, 'auditLogs'), {
                                        actorType: 'user',
                                        actorId: getDeviceId(),
                                        actorName: name.trim() || null,
                                        action: 'rsvp_updated',
                                        entityType: 'rsvp',
                                        entityId: existingRSVP.id,
                                        entityLabel: formatEventLabel(selectedEvent),
                                        before: { plusCount: prevPlusCount },
                                        after: { plusCount: parseInt(val) },
                                        metadata: { reason: 'guest count edit', source: 'web' },
                                        timestamp: Timestamp.now()
                                      });
                                    })
                                    .catch(err => console.error('Guest update failed:', err));
                                }
                              }}
                            >
                              {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <label>guests</label>
                          </div>
                          {/* Name/guests status icon — far right, same row */}
                          {hasRSVPd && name.trim() && (
                            <span style={{flexShrink:0, marginLeft:'auto'}}>
                              {savedIndicator
                                ? <span style={{fontSize:'14px', animation:'spin 1s linear infinite', display:'inline-block', color:'rgba(255,255,255,.6)'}}>⟳</span>
                                : <span style={{color:'#4ade80', fontSize:'14px', fontWeight:'700'}}>✓</span>
                              }
                            </span>
                          )}
                        </div>
                      </div>

                      {/* SMS opt-in */}
                      <div className="rsvp-expansion-section rsvp-expansion-divider">
                        {/* Count sits on the label row's unused right side — no added height.
                            The sublabel below is left alone: naming weather/location changes
                            is the strongest reason to subscribe, so it isn't filler to reuse. */}
                        <div className="rsvp-expansion-label" style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px'}}>
                          <span>Text reminders <span style={{fontWeight:400, opacity:0.55, fontSize:'11px'}}>(optional)</span></span>
                          {showJoinedPill && <span style={joinedPillStyle}>{subscriberCount} players subscribed</span>}
                        </div>
                        <div className="rsvp-expansion-sublabel" style={{marginBottom:'0.4rem'}}>
                          <span style={{color:'rgba(255,200,100,.75)', fontWeight:500}}>Weather alerts &amp; location changes</span>
                          <span style={{color:'rgba(255,255,255,.4)'}}> are also texted.</span>
                        </div>
                        <div style={{display:'flex', alignItems:'center', gap:'0.5rem', marginTop:'0.4rem'}}>
                          <input
                            type="tel"
                            className="sms-optin-input"
                            value={smsPhone}
                            onChange={e => handleSmsPhoneChange(e.target.value)}
                            placeholder="(360) 555-0100"
                            style={{width:'150px', flexShrink:0, maxWidth:'150px'}}
                          />
                          {smsStatus === 'checking' && (
                            <span style={{fontSize:'14px', animation:'spin 1s linear infinite', display:'inline-block'}}>⟳</span>
                          )}
                          {smsPhone.length > 0 && smsStatus === 'opted-in' && (
                            <span style={{color:'#4ade80', fontSize:'14px', fontWeight:'700'}}>✓</span>
                          )}
                          {smsPhone.length > 0 && smsStatus === null && toE164(smsPhone) === '' && (
                            <span style={{color:'rgba(255,255,255,.35)', fontSize:'13px'}}>✕</span>
                          )}
                        </div>
                        {smsPhone.length > 0 && smsStatus === 'opted-out' && (
                          <div className="sms-optin-hint sms-optin-stopped" style={{marginTop:'0.3rem'}}>
                            This number has unsubscribed. To re-subscribe, text START to (888) 968-4999.
                          </div>
                        )}
                        {smsPhone.length > 0 && smsStatus !== 'opted-out' && smsStatus !== 'checking' && (
                          <label className="sms-optin-label" style={{marginTop:'0.35rem'}}>
                            <input
                              type="checkbox"
                              checked={smsOptIn}
                              onChange={e => {
                                const checked = e.target.checked;
                                setSmsOptIn(checked);
                                if (smsPhone) {
                                  saveSubscriber(smsPhone, checked);
                                  if (checked) trackSmsSubscribed(); else trackSmsUnsubscribed();
                                  showToast('Subscription updated!');
                                  if (checked) setSmsStatus('opted-in');
                                  else setSmsStatus(null);
                                }
                              }}
                            />
                            <span>
                              {SITE.smsOptInLabel}
                              {smsOptIn && smsStatus === 'opted-in' && (
                                <span className="sms-optin-badge">✓ subscribed</span>
                              )}
                            </span>
                          </label>
                        )}
                        {/* Wording and the reason it is long: see SMS_DISCLOSURE. */}
                        <div className="sms-optin-fine-print">{SMS_DISCLOSURE}</div>
                      </div>

                      {/* Calendar + Share — single de-emphasized footer row */}
                      <div style={{
                        display:'flex', alignItems:'center', justifyContent:'space-between',
                        paddingTop:'9px', marginTop:'2px',
                        borderTop:'0.5px solid rgba(255,255,255,.07)',
                        opacity:0.75,
                      }}>
                        {/* Calendar links */}
                        <div style={{display:'flex', alignItems:'center', gap:'4px'}}>
                          <span style={{fontSize:'11px', color:'rgba(255,255,255,.3)', marginRight:'3px'}}>Calendar:</span>
                          <svg width="11" height="11" viewBox="0 0 16 16" fill="none" style={{flexShrink:0, marginRight:'1px'}}>
                            <rect x="1" y="3" width="14" height="12" rx="2" stroke="rgba(120,180,255,.55)" strokeWidth="1.5"/>
                            <path d="M1 7h14" stroke="rgba(120,180,255,.55)" strokeWidth="1.5"/>
                            <path d="M5 1v4M11 1v4" stroke="rgba(120,180,255,.55)" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          {selectedEvent && (
                            <>
                              <button style={{fontSize:'11px', color:'rgba(120,180,255,.6)', background:'none', border:'none', borderBottom:'0.5px solid rgba(120,180,255,.2)', padding:0, fontFamily:'inherit', cursor:'pointer', lineHeight:1.4}}
                                onClick={() => { trackCalendarGoogle('single'); const url = googleCalendarUrl(selectedEvent, locations[selectedEvent.primaryLocationId], siteUrl); window.open(url,'_blank','noopener,noreferrer'); }}>
                                This game
                              </button>
                              <span style={{fontSize:'10px', color:'rgba(255,255,255,.15)'}}>·</span>
                            </>
                          )}
                          {recurringSettings?.enabled && (
                            <button style={{fontSize:'11px', color:'rgba(120,180,255,.6)', background:'none', border:'none', borderBottom:'0.5px solid rgba(120,180,255,.2)', padding:0, fontFamily:'inherit', cursor:'pointer', lineHeight:1.4}}
                              onClick={() => {
                                trackCalendarDownload('series');
                                const ics = generateSeriesICS(recurringSettings, locations[selectedEvent?.primaryLocationId], siteUrl, calendarDescription);
                                if (ics) { downloadICS(ics, 'pickup-ultimate-schedule.ics'); showToast('Open the file to import. Google Calendar: Other calendars → Import'); }
                              }}>
                              Full schedule
                            </button>
                          )}
                        </div>

                        {/* Share */}
                        <button
                          style={{fontSize:'11px', color:'rgba(120,180,255,.6)', background:'none', border:'none', borderBottom:'0.5px solid rgba(120,180,255,.2)', padding:0, fontFamily:'inherit', cursor:'pointer', lineHeight:1.4, display:'flex', alignItems:'center', gap:'4px'}}
                          onClick={() => {
                            const base = siteUrl || window.location.origin;
                            const url = `${base}?event=${selectedEvent.id}`;
                            navigator.clipboard.writeText(url).then(() => { trackShareLink(); showToast('Link copied!'); }).catch(() => showToast('Copy failed — use your browser to copy the URL', 'error'));
                          }}
                        >
                          <svg width="11" height="11" viewBox="0 0 16 16" fill="none" style={{flexShrink:0}}>
                            <circle cx="13" cy="3" r="2" stroke="rgba(120,180,255,.55)" strokeWidth="1.5"/>
                            <circle cx="3" cy="8" r="2" stroke="rgba(120,180,255,.55)" strokeWidth="1.5"/>
                            <circle cx="13" cy="13" r="2" stroke="rgba(120,180,255,.55)" strokeWidth="1.5"/>
                            <path d="M5 7l6-3M5 9l6 3" stroke="rgba(120,180,255,.55)" strokeWidth="1.5"/>
                          </svg>
                          Share
                        </button>
                      </div>

                      </>)}{/* end yes/maybe guard */}

                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div style={{
              background: 'var(--band-default)',
              display:'flex', flexDirection:'column', alignItems:'center',
              padding:'1.5rem 1.25rem 1.25rem', borderRadius:0,
            }}>
              <div style={{textAlign:'center'}}>
                <div style={{fontSize:'22px', marginBottom:'8px'}}>🚫</div>
                <div style={{fontSize:'15px', fontWeight:600, color:'white', lineHeight:1.25}}>
                  RSVPs are disabled for this event.
                </div>
              </div>
            </div>
          )}

          </> }{/* end normal content */}

          </div> {/* end evt-lockable */}

        </div>
      </div>
    );
  };

  // ─── RENDER CALENDAR GRID ─────────────────────────────────────
  const renderCalendar = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();

    // Build day -> events map
    const eventsByDay = {};
    calendarEvents.forEach(ev => {
      const d = ev.date.toDate().getDate();
      if (!eventsByDay[d]) eventsByDay[d] = [];
      eventsByDay[d].push(ev);
    });

    // Build grid cells
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const isSelected = (day) =>
      selectedEvent &&
      selectedEvent.date.toDate().getDate() === day &&
      selectedEvent.date.toDate().getMonth() === month &&
      selectedEvent.date.toDate().getFullYear() === year;

    return (
      <div className={`cal-wrapper${!selectedEvent ? ' cal-wrapper-standalone' : ''}`}>
        <div className="cal-header">
          <button className="cal-nav" onClick={() => {
            const d = new Date(year, month - 1, 1);
            setCalendarDate(d);
            loadCalendarEvents(d, true);
          }}>‹</button>
          <span className="cal-month-label">{format(calendarDate, 'MMMM yyyy')}</span>
          <button className="cal-nav" onClick={() => {
            const d = new Date(year, month + 1, 1);
            setCalendarDate(d);
            loadCalendarEvents(d, true);
          }}>›</button>
        </div>

        <div className="cal-grid">
          {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
            <div key={d} className="cal-dow">{d}</div>
          ))}
          {cells.map((day, i) => {
            if (!day) return <div key={`empty-${i}`} className="cal-cell empty" />;
            const dayEvents = eventsByDay[day] || [];
            const hasEvent = dayEvents.length > 0;
            const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
            // isPast: days before today are always past; today is past only if the event has ended
            const isPast = (() => {
              const calDay = new Date(year, month, day);
              const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
              if (calDay < todayDay) return true;
              if (calDay > todayDay) return false;
              // Same day as today — check if event end time has passed
              if (!hasEvent) return false;
              const ev = dayEvents[0];
              const evDate = ev.date.toDate();
              const evEnd = getEventEndTime(ev);
              return evEnd < today;
            })();
            const isSpecial = dayEvents.some(e => e.isSpecial);
            const isCanceled = hasEvent && dayEvents.every(e => e.isCanceled);

            return (
              <div
                key={day}
                className={[
                  'cal-cell',
                  isToday ? 'cal-today' : '',
                  isPast ? 'cal-past' : '',
                  hasEvent ? 'cal-has-event' : '',
                  isSpecial ? 'cal-special' : '',
                  isCanceled ? 'cal-canceled' : '',
                  isSelected(day) ? 'cal-selected' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => {
                  if (!hasEvent) return;
                  if (isSelected(day)) {
                    // Already selected — pulse the card to acknowledge
                    setPulseCard(true);
                    setTimeout(() => setPulseCard(false), 600);
                  } else {
                    setSelectedEvent(dayEvents[0]);
                  }
                }}
              >
                {isPast && hasEvent && dayEvents[0].actualAttendance ? (
                  // Past game with attendance — full-cell dark display
                  <div className="cal-cell-attendance">
                    <span className="cal-cell-attendance-label">We Had</span>
                    <span className="cal-cell-attendance-num">{dayEvents[0].actualAttendance}</span>
                    <span className="cal-cell-attendance-players">Players</span>
                  </div>
                ) : (
                  <>
                    {/* Date number always in top-left corner */}
                    <span className="cal-day-num">{day}</span>
                    {/* Future event: show title instead of dot */}
                    {hasEvent && !isPast && (
                      <span className="cal-event-title">
                        {dayEvents[0].title || 'Game'}
                      </span>
                    )}
                    {/* Past event with no attendance: show title faded */}
                    {hasEvent && isPast && !dayEvents[0].actualAttendance && (
                      <span className="cal-event-title">
                        {dayEvents[0].title || 'Game'}
                      </span>
                    )}
                    {/* Special star in corner (future events only — past with attendance uses full cell) */}
                    {isSpecial && !isPast && <span className="cal-special-star">★</span>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── MAIN RENDER ─────────────────────────────────────────────
  return (
    <>
      {renderEventCard()}
      {renderCalendar()}

      {/* Location popup */}
      {showLocationPopup && (
        <div className="location-popup-overlay" onClick={() => setShowLocationPopup(false)}>
          <div className="location-popup" onClick={e => e.stopPropagation()}>
            <button className="popup-close" onClick={() => setShowLocationPopup(false)}>×</button>
            <h2>Event Locations</h2>
            {popupLocations.map((loc, index) => (
              <div key={loc.id} className="popup-location-card">
                <div className="popup-location-header">
                  <h3>{loc.name}</h3>
                  <span className="location-role-badge">{loc.role}</span>
                </div>
                {loc.description && (
                  <p className="popup-location-description">{loc.description}</p>
                )}
                <div className="popup-location-address">
                  <strong>Address:</strong>{' '}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="maps-link"
                  >
                    {loc.address} 📍
                  </a>
                </div>
                {loc.imageUrl && (
                  <img src={loc.imageUrl} alt={loc.name} className="popup-location-image" />
                )}
                {index < popupLocations.length - 1 && <hr />}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default EventCalendar;
